/**
 * BookCapacityBanner — the over-plan nudge for a Free-tier pro whose
 * agent-added contact book is at or over the plan's MaxClients ceiling.
 *
 * Product contract (2026-08-24 book-capacity design): the cap NEVER blocks a
 * consumer-initiated connect — a referral/invite signup always lands its
 * roster row (PR #633). This banner is where that soft-exceed becomes
 * visible: whole-book framing ("31 people — plan supports 25"), no lock
 * implied, because nothing IS locked. The CTA carries no price copy — the
 * pricing page renders the resolved activation offer (ADR-040: never
 * re-derive price copy client-side).
 *
 * Mounted ONCE by `ProLayout` (mirrors UnusedDiscountBanner's mount) on every
 * authed page except `/pricing` (the CTA's own destination) and `/welcome`.
 *
 * Visibility (ALL must hold):
 *   - `useBookCapacity()` resolved a capped plan (`null` = loading / error /
 *     unlimited — FAIL QUIET, hidden beats wrong).
 *   - `agent_added_count >= max_clients` (at-cap counts: the next add is
 *     blocked and the next connect soft-exceeds).
 *   - Not dismissed this browser session (`sessionStorage` — it reappears
 *     next session while the condition holds, matching NoStandingOffersBanner
 *     rather than UnusedDiscountBanner's durable dismiss: capacity changes as
 *     the book changes, so a permanent dismiss could hide a live state).
 */
import { Users, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { usePostHog } from "@posthog/react";
import { useBookCapacity } from "../../hooks/useBookCapacity";

const DISMISS_KEY = "sill.bookCapacityBanner.dismissed";

function readDismissed(): boolean {
  try {
    return sessionStorage.getItem(DISMISS_KEY) === "1";
  } catch {
    return false;
  }
}

function writeDismissed(): void {
  try {
    sessionStorage.setItem(DISMISS_KEY, "1");
  } catch {
    // Best-effort only.
  }
}

export function bookCapacityBannerLine(count: number, cap: number): string {
  if (count > cap) {
    return `Your book has ${count} people — the Free plan supports ${cap}.`;
  }
  return `Your book is at the Free plan limit (${count} of ${cap}).`;
}

export function BookCapacityBanner() {
  const capacity = useBookCapacity();
  const posthog = usePostHog();
  const [dismissed, setDismissed] = useState<boolean>(() => readDismissed());
  const shownCapturedRef = useRef(false);

  const atOrOverCap =
    capacity !== null &&
    capacity.max_clients > 0 &&
    capacity.agent_added_count >= capacity.max_clients;
  const visible = atOrOverCap && !dismissed;

  useEffect(() => {
    if (visible && !shownCapturedRef.current) {
      shownCapturedRef.current = true;
      posthog?.capture("crm_billing.book_capacity_banner_shown", {
        over_by: capacity.agent_added_count - capacity.max_clients,
      });
    }
  }, [visible, capacity, posthog]);

  if (!visible) return null;

  function handleDismiss() {
    writeDismissed();
    setDismissed(true);
    posthog?.capture("crm_billing.book_capacity_banner_dismissed");
  }

  function handleCtaClick() {
    posthog?.capture("crm_billing.upgrade_cta_clicked", {
      source: "book_capacity_banner",
    });
  }

  return (
    <div
      role="status"
      data-testid="book-capacity-banner"
      className="flex items-center gap-3 border-b border-warning/30 bg-warning/10 px-4 py-3 text-sm sm:px-8"
    >
      <Users
        aria-hidden="true"
        className="h-4 w-4 flex-shrink-0 text-warning"
      />
      <p className="min-w-0 flex-1 text-fg">
        {bookCapacityBannerLine(
          capacity.agent_added_count,
          capacity.max_clients,
        )}{" "}
        New clients who connect with you still land in your book.{" "}
        <Link
          to="/pricing?src=book_capacity_banner"
          onClick={handleCtaClick}
          className="whitespace-nowrap font-semibold text-pro underline underline-offset-2"
        >
          See plans
        </Link>
      </p>
      <button
        type="button"
        aria-label="Dismiss"
        onClick={handleDismiss}
        className="flex-shrink-0 rounded-md p-1 text-fg-muted transition hover:bg-warning/20 hover:text-fg"
      >
        <X className="h-4 w-4" aria-hidden="true" />
      </button>
    </div>
  );
}
